import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import NumberFormat from 'react-number-format';
import { simappColors } from 'shared/lib/theme';
import heConstants from '../../../shared/lib/resources/he-constants';

const styles = {
  defaultTextField: {
    width: '100%',
    backgroundColor: simappColors.inputYellowColor,
    padding: '6px 2px 3px 3px',
    textAlign: 'center',
    fontSize: '1rem',
    border: 'none',
    fontWeight: '500',
    fontFamily: 'helvetica-neue-lt, sans-serif',
    whiteSpace: 'nowrap',
    '&:focus': {
      outline: 'none'
    },
    '&.right': {
      textAlign: 'right',
      paddingRight: '10px'
    }
  },
  changedTextField: {
    backgroundColor: simappColors.inputLightRedColor
  },
  readonlyTextField: {
    float: 'right',
    backgroundColor: simappColors.palerGrey,
    '&.positive': {
      backgroundColor: simappColors.copeptinGreen
    },
    '&.negative': {
      backgroundColor: simappColors.copeptinRed
    }
  },
  textRight: {
    textAlign: 'right',
    paddingRight: '10px'
  }
};

const thousandSeparatorEU = '.';
const thousandSeparatorUS = ',';
const decimalSeparatorEU = ',';
const decimalSeparatorUS = '.';

class HeInputField extends Component {
  prefix;
  suffix;
  valueMultiplier;
  decimalSeparator = decimalSeparatorEU;
  thousandSeparator = thousandSeparatorEU;

  decimalScale = 2;
  fixedDecimalScale = false;

  constructor(props) {
    super(props);
    this.state = { value: props.value };
  }

  setSeparators() {
    if (this.props.decimalSeparator === undefined) {
      this.decimalSeparator = this.props.isUSNumberInput ? decimalSeparatorUS : decimalSeparatorEU;
    } else {
      this.decimalSeparator = this.props.decimalSeparator;
    }
    this.decimalScale = this.props.decimalScale;
    if (this.props.thousandSeparator === undefined) {
      this.thousandSeparator = this.props.isUSNumberInput ? thousandSeparatorUS : thousandSeparatorEU;
    } else {
      this.thousandSeparator = this.props.thousandSeparator;
    }
  }

  onValueChange = (values) => {
    const { floatValue } = values;
    // avoid NaN, only undefined is recognised as default in the store
    let value;
    if (floatValue !== undefined) {
      value = floatValue / this.valueMultiplier;
    }
    if (value !== this.state.value || value === undefined) {
      this.props.onValueChange(this.props.fieldName, this.props.fieldIndex, this.enforceMaxValue(value));
    }
  }

  setSuffixAndPrefix() {
    if (this.props.type === heConstants.HeTableRowCurrency) {
      if (this.props.isUSNumberInput) {
        this.prefix = this.getCurrencyValue();
        this.suffix = '';
      } else {
        this.suffix = this.getCurrencyValue();
        this.prefix = '';
      }
      this.decimalScale = 2;
      this.fixedDecimalScale = true;
    } else if (this.props.type === heConstants.HeTableRowPercentage) {
      this.prefix = '';
      this.suffix = ' %';
    } else if (this.props.type === heConstants.HeTableRowDays || this.props.type === heConstants.HeTableRowTime) {
      this.prefix = '';
      this.suffix = '';
    }
  }

  getClassName(isChanged) {
    if (this.props.displayType === 'text') {
      return (`${this.props.classes.defaultTextField} ${this.props.classes.readonlyTextField} ${this.getColorCode()}${this.props.textAlign === 'right' ? ' right' : ''}`);
    }
    if (!this.props.markChanged || !isChanged) {
      return (`${this.props.classes.defaultTextField}${this.props.textAlign === 'right' ? ' right' : ''}`);
    }
    return (`${this.props.classes.defaultTextField} ${this.props.classes.changedTextField}${this.props.textAlign === 'right' ? ' right' : ''}`);
  }

  getCurrencyValue() {
    if (this.props.isUSNumberInput) {
      return '$';
    }
    return ' €';
  }

  getColorCode = () => {
    const { positiveColorCode } = this.props;
    if (positiveColorCode !== undefined) {
      if (positiveColorCode) {
        if (this.props.value >= 0.01) {
          return 'positive';
        }
        if (this.props.value <= -0.01) {
          return 'negative';
        }
        return '';
      }
      if (this.props.value >= 0.01) {
        return 'negative';
      }
      if (this.props.value <= -0.01) {
        return 'positive';
      }
    }
    return '';
  }

  enforceMaxValue(input) {
    let value = input;
    if (this.props.maxValue > 0 && value > this.props.maxValue) {
      value = this.props.maxValue;
    }
    this.setState({ value });
    return value;
  }

  setValueMultiplier() {
    if (this.props.type === heConstants.HeTableRowPercentage) {
      this.valueMultiplier = 100;
    } else {
      this.valueMultiplier = 1;
    }
  }

  render() {
    this.setSeparators();
    this.setSuffixAndPrefix();
    this.setValueMultiplier();
    const className = this.getClassName(this.props.isChanged);
    const value = this.props.value !== null && this.props.displayType !== 'input'
      ? (this.props.value * this.valueMultiplier).highNumberFix(this.decimalScale)
      : (this.props.value * this.valueMultiplier);
    const inputField = (
      <NumberFormat
        id={this.props.fieldName}
        className={className}
        value={value}
        isNumericString
        prefix={this.prefix}
        suffix={this.suffix}
        displayType={this.props.displayType}
        decimalScale={this.decimalScale}
        decimalSeparator={this.decimalSeparator}
        thousandSeparator={this.thousandSeparator}
        onValueChange={this.onValueChange}
        onClick={event => this.props.displayType === 'input' && event.target.select()}
        fixedDecimalScale={this.fixedDecimalScale}
        allowNegative={this.props.allowNegative}
        maxLength={22}
      />
    );
    return (this.props.displayType === 'text'
      ? <div>{inputField}</div> : inputField);
  }
}

HeInputField.propTypes = {
  classes: PropTypes.object.isRequired,
  fieldName: PropTypes.string,
  fieldIndex: PropTypes.number,
  isUSNumberInput: PropTypes.bool,
  displayType: PropTypes.string,
  isChanged: PropTypes.bool,
  markChanged: PropTypes.bool,
  onValueChange: PropTypes.func,
  value: PropTypes.any,
  type: PropTypes.string,
  maxValue: PropTypes.number,
  allowNegative: PropTypes.bool,
  decimalSeparator: PropTypes.string,
  decimalScale: PropTypes.number,
  thousandSeparator: PropTypes.string,
  textAlign: PropTypes.string,
  positiveColorCode: PropTypes.bool,
};

HeInputField.defaultProps = {
  fieldName: '',
  fieldIndex: -1,
  isUSNumberInput: false,
  onValueChange: () => { },
  value: null,
  isChanged: false,
  markChanged: true,
  allowNegative: false,
  type: 'currency',
  maxValue: -1,
  displayType: 'input',
  decimalSeparator: undefined,
  decimalScale: 2,
  thousandSeparator: undefined,
  textAlign: 'center',
  positiveColorCode: undefined
};

export default withStyles(styles)(HeInputField);
